/**
 * /download/movie/$slug — clean movie download page.
 *
 * Same safe-link rules as /download/$slug/$ep: every explicit download URL is
 * accepted as-is. It can be a protected /safelink URL, provider landing page,
 * or direct file URL. No scraping, proxying, or blob-download is required.
 */
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Download, ArrowLeft, Loader2, AlertTriangle } from "lucide-react";
import { Header } from "@/components/rawan/Header";
import { Footer } from "@/components/rawan/Footer";
import { useI18n, t as tt } from "@/lib/i18n";
import { getMovieBySlug } from "@/lib/movies-data";
import { startDirectDownload } from "@/lib/start-direct-download";

export const Route = createFileRoute("/download/movie/$slug")({
  head: () => ({
    meta: [
      { title: "تحميل الفيلم · Download Movie · Télécharger — Rawan UI" },
      { name: "description", content: "صفحة تحميل الفيلم بجودات متعددة عبر بروكسي محمي." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: DownloadMoviePage,
});

type Row = {
  key: string;
  quality: string;
  server: string;
  shareUrl: string;
  filenameBase: string;
  size?: string;
};

function DownloadMoviePage() {
  const { slug } = useParams({ from: "/download/movie/$slug" });
  const { language } = useI18n();
  const t = (ar: string, en: string, fr: string) => tt(language, ar, en, fr);
  const movie = useMemo(() => getMovieBySlug(slug), [slug]);

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows([]);

    async function run() {
      if (!movie) {
        setError(t("لم يتم العثور على الفيلم.", "Movie not found.", "Film introuvable."));
        setLoading(false);
        return;
      }
      const filenameBase = `${movie.slug}-${movie.year || ""}`.trim().replace(/-$/, "");

      if (!Array.isArray(movie.downloads) || movie.downloads.length === 0) {
        setError(
          t(
            "لا تتوفر روابط تحميل لهذا الفيلم. أضف لكل جودة رابطها الخاص من لوحة التحكم، ويفضّل أن يكون رابطاً محمياً من مولّد الروابط.",
            "No download links for this movie. Add one URL per quality in the dashboard; protected Safelink URLs are recommended.",
            "Aucun lien de téléchargement. Ajoutez une URL par qualité depuis le tableau de bord; les liens protégés Safelink sont recommandés.",
          ),
        );
        setLoading(false);
        return;
      }

      const results = movie.downloads
        .filter((d) => /^https?:\/\//i.test(d.url))
        .map((d, i): Row => ({
          key: `d-${i}`,
          quality: d.quality,
          server: d.server || providerLabelFromUrl(d.url),
          shareUrl: d.url,
          filenameBase: `${filenameBase}-${d.quality}`,
          size: d.size,
        }));
      if (cancelled) return;
      const collected = results;
      if (!collected.length) {
        setError(
          t(
            "روابط التحميل غير صالحة. تأكد أن كل رابط يبدأ بـ http أو https.",
            "Download links are invalid. Make sure every link starts with http or https.",
            "Les liens de téléchargement sont invalides. Vérifiez que chaque lien commence par http ou https.",
          ),
        );
      } else {
        setRows(collected);
      }
      setLoading(false);
    }

    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const title =
    language === "en" && movie?.titleEn
      ? movie.titleEn
      : language === "fr" && movie?.titleFr
        ? movie.titleFr
        : (movie?.title ?? slug);

  return (
    <div className="min-h-dvh flex flex-col bg-background text-foreground">
      <Header />
      <main className="flex-1 mx-auto w-full max-w-3xl px-4 py-6 space-y-5">
        <Link
          to="/movie/$slug"
          params={{ slug }}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("العودة إلى الفيلم", "Back to movie", "Retour au film")}
        </Link>

        <header className="rounded-lg border border-border bg-card p-4">
          <div className="text-xs text-muted-foreground">{movie?.year}</div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Download className="h-5 w-5" />
            {title}
          </h1>
        </header>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive p-3 text-sm flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length ? (
          <section className="rounded-lg border border-border bg-card overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="text-start p-3">{t("الجودة", "Quality", "Qualité")}</th>
                  <th className="text-start p-3">{t("الخادم", "Server", "Serveur")}</th>
                  <th className="text-start p-3">{t("الحجم", "Size", "Taille")}</th>
                  <th className="text-end p-3">{t("رابط", "Link", "Lien")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-t border-border hover:bg-accent/30">
                    <td className="p-3 font-semibold">{r.quality}</td>
                    <td className="p-3">{r.server}</td>
                    <td className="p-3 text-xs text-muted-foreground">{r.size ?? "—"}</td>
                    <td className="p-3 text-end">
                      <DownloadButton
                        shareUrl={r.shareUrl}
                        filenameBase={r.filenameBase}
                        label={t("تحميل", "Download", "Télécharger")}
                        loadingLabel={t("جارٍ التجهيز…", "Preparing…", "Préparation…")}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        <p className="text-[11px] text-muted-foreground">
          {t(
            "كل زر يفتح رابط الجودة المحفوظ كما هو. استخدم مولّد الرابط المحمي إذا أردت إخفاء الرابط الأصلي.",
            "Each button opens the saved quality URL as-is. Use the Safelink generator if you want to hide the original URL.",
            "Chaque bouton ouvre l'URL de qualité enregistrée telle quelle. Utilisez le générateur Safelink pour masquer l'URL originale.",
          )}
        </p>
      </main>
      <Footer />
    </div>
  );
}

function providerLabelFromUrl(raw: string): string {
  try {
    const host = new URL(raw).hostname.toLowerCase();
    if (host.includes("mediafire")) return "MediaFire";
    if (host.includes("drive.google")) return "Google Drive";
    if (host.includes("dropbox")) return "Dropbox";
    if (host.includes("1drv") || host.includes("onedrive")) return "OneDrive";
    if (host.includes("mega")) return "MEGA";
    if (host.includes("terabox") || host.includes("1024tera")) return "TeraBox";
    if (host.includes("r2.dev") || host.includes("cloudflarestorage")) return "R2";
    if (typeof window !== "undefined" && host === window.location.hostname) return "Safelink";
    return host.replace(/^www\./, "");
  } catch {
    return "Server";
  }
}

function DownloadButton({
  shareUrl,
  filenameBase,
  label,
  loadingLabel,
}: {
  shareUrl: string;
  filenameBase: string;
  label: string;
  loadingLabel: string;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setErr(null);
          setBusy(true);
          const res = await startDirectDownload(shareUrl, `${filenameBase}.mp4`);
          setBusy(false);
          if (!res.ok) setErr(res.error);
        }}
        className="inline-flex items-center gap-1 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-xs hover:bg-primary/90 disabled:opacity-60 disabled:cursor-wait"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
        {busy ? loadingLabel : label}
      </button>
      {err && <span className="text-[10px] text-destructive max-w-[220px] text-end">{err}</span>}
    </div>
  );
}
